import { z } from 'zod';
import { IdSchema } from './encoding.js';
/** The five designated categories of sensitive data (SDD §12.1). */
export const SensitivityCategorySchema = z.enum([
    'salary_compensation',
    'bank_details',
    'identity',
    'trn',
    'confidential_hr',
]);
/** The authorization vocabulary (ERD §8.1). Roles group these; code checks only these. */
export const PermissionActionSchema = z.enum([
    'view',
    'create',
    'change',
    'post',
    'approve',
    'export',
    'view_unmasked',
    'use_ai_use_case',
]);
const CodeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/, 'must be lower snake case')
    .max(50);
const RecordTypeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/, 'must be <module>.<record>')
    .max(100);
/**
 * `/api/v1/me` (SDD §22.3): who the caller is, the CSRF token for unsafe methods, the permission
 * keys the SPA uses to hide what the user cannot do, and the business time zone.
 * Capabilities are **UX only**; the API remains the enforcement point (SDD §22.4).
 */
export const MeResponseSchema = z.object({
    userId: IdSchema,
    displayName: z.string(),
    email: z.email(),
    csrfToken: z.string().min(1),
    capabilities: z.array(z.string()),
    /**
     * The capabilities the caller holds **only** through grants limited to part of the organisation
     * or to their own records (FRD-USR-007, USR-023): a type-level summary (a count of a whole list)
     * is not available to them. UX only, like `capabilities`.
     */
    scopedCapabilities: z.array(z.string()).optional(),
    /**
     * The capabilities the caller holds **only** through own-records-only grants (self-service,
     * FRD-USR-023, HRP-021): the SPA offers the "my …" pages for these, not the management lists.
     * UX only.
     */
    ownRecordsCapabilities: z.array(z.string()).optional(),
    businessTimezone: z.string(),
    /**
     * The largest document the API accepts, in bytes (SDD §12.5): upload forms state it and refuse a
     * larger file before sending it. The API enforces it either way.
     */
    documentMaxUploadBytes: z.number().int().positive().optional(),
});
/**
 * `/auth/methods`: which sign-in methods this deployment has configured, so the sign-in screen
 * offers only those. `csrfToken` is present only when the temporary local login is enabled
 * (SDD §11.1.1): its POST is an unsafe method and carries the token like any other.
 */
export const AuthMethodsResponseSchema = z.object({
    oidc: z.boolean(),
    local: z.boolean(),
    /** Built-in sign-in, password plus authenticator app (SDD §11.1.2). Issues `csrfToken`. */
    password: z.boolean().optional(),
    csrfToken: z.string().min(1).optional(),
});
/** The temporary local login (SDD §11.1.1). The password is bounded only against abuse. */
export const LocalLoginRequestSchema = z.strictObject({
    email: z.email().max(254),
    password: z.string().min(1).max(1024),
});
// ── Built-in sign-in: password plus authenticator app (SDD §11.1.2; FRD-USR-024 to 029) ────────
/** Passwords are bounded only against abuse; the configured policy is checked by the API. */
const PasswordSchema = z.string().min(1).max(1024);
const TotpCodeSchema = z
    .string()
    .regex(/^\d{6}$/, 'must be the 6-digit code from the authenticator');
const RecoveryCodeSchema = z.string().min(1).max(64);
/** A setup-link token: 32 random bytes, base64url. Sent in a request body, never in a URL. */
const SetupTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'is not a setup link token');
/**
 * Step one of sign-in: answered 202 with the second factor to ask for, or 200 with `none` when the
 * second factor is not required and the password has signed the user in (SDD §11.1.2, CLR-42).
 * `enrol` is the user who has no authenticator while one is required: the answer carries the
 * authenticator to add, and `POST /auth/password-login/enrol` finishes the sign-in with its code.
 */
export const PasswordLoginRequestSchema = z.strictObject({
    email: z.email().max(254),
    password: PasswordSchema,
});
/** Step two: exactly one of an authenticator code or a recovery code. */
export const SecondFactorRequestSchema = z.union([
    z.strictObject({ code: TotpCodeSchema }),
    z.strictObject({ recoveryCode: RecoveryCodeSchema }),
]);
export const SecondFactorResponseSchema = z.object({
    factor: z.enum(['otp', 'recovery_code']),
    recoveryCodesRemaining: z.number().int().min(0),
});
/** Issued by an administrator; the token is shown once and never stored by the system. */
export const SetupLinkResponseSchema = z.object({
    token: SetupTokenSchema,
    expiresAt: z.iso.datetime({ offset: true }),
    /** True when an existing credential was revoked (a reset). */
    reset: z.boolean(),
});
export const CredentialStatusSchema = z.object({
    state: z.enum(['none', 'active', 'locked', 'revoked']),
    lockedUntil: z.iso.datetime({ offset: true }).nullable(),
    passwordChangedAt: z.iso.datetime({ offset: true }).nullable(),
    recoveryCodesRemaining: z.number().int().min(0),
    setupLinkExpiresAt: z.iso.datetime({ offset: true }).nullable(),
    /** False for a credential set up with a password alone. */
    authenticatorEnrolled: z.boolean(),
});
export const SetupTokenRequestSchema = z.strictObject({ token: SetupTokenSchema });
export const SetupLinkInfoSchema = z.object({
    email: z.email(),
    displayName: z.string(),
    expiresAt: z.iso.datetime({ offset: true }),
    step: z.enum(['password', 'authenticator']),
    /** Whether an authenticator is added after the password. */
    secondFactor: z.boolean(),
});
export const SetupPasswordRequestSchema = z.strictObject({
    token: SetupTokenSchema,
    password: PasswordSchema,
});
/** The authenticator enrolment: the `otpauth://` URI (rendered as a QR code) and the manual key. */
export const AuthenticatorEnrolmentSchema = z.object({
    otpauthUri: z.string().startsWith('otpauth://totp/'),
    manualKey: z.string().regex(/^[A-Z2-7]+$/),
    accountName: z.string(),
});
export const PasswordLoginPendingSchema = z.union([
    z.object({ secondFactor: z.enum(['totp', 'none']) }),
    z.object({ secondFactor: z.literal('enrol'), enrolment: AuthenticatorEnrolmentSchema }),
]);
/** The first code from the authenticator added at sign-in. */
export const EnrolAtSignInRequestSchema = z.strictObject({ code: TotpCodeSchema });
/**
 * The answer to the setup link's password step: the authenticator to enrol, or `complete` when the
 * second factor is not required and the password alone finished the setup.
 */
export const SetupPasswordResponseSchema = z.union([
    AuthenticatorEnrolmentSchema,
    z.object({ complete: z.literal(true) }),
]);
export const SetupConfirmRequestSchema = z.strictObject({
    token: SetupTokenSchema,
    code: TotpCodeSchema,
});
/** Shown once, at setup or replacement; only their hashes are kept. */
export const RecoveryCodesResponseSchema = z.object({
    recoveryCodes: z.array(z.string()).min(1),
});
export const ChangePasswordRequestSchema = z.strictObject({
    currentPassword: PasswordSchema,
    newPassword: PasswordSchema,
    /** Needed while the second factor is required. */
    code: TotpCodeSchema.optional(),
});
export const ReplaceRecoveryCodesRequestSchema = z.strictObject({ code: TotpCodeSchema });
export const OwnCredentialSchema = z.object({
    builtIn: z.boolean(),
    recoveryCodesRemaining: z.number().int().min(0),
    /** Whether a password change is confirmed with an authenticator code. */
    secondFactor: z.boolean(),
});
export const CreateUserRequestSchema = z.strictObject({
    email: z.email().max(254),
    displayName: z.string().min(1).max(200),
    employeeId: IdSchema.optional(),
});
export const CreateRoleRequestSchema = z.strictObject({
    code: CodeSchema,
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    isPrivileged: z.boolean().optional(),
});
const GrantPermissionFields = {
    recordType: RecordTypeSchema,
    action: PermissionActionSchema,
    field: z.string().max(100).optional(),
    sensitivityCategory: SensitivityCategorySchema.optional(),
    /** Self-service: the grant reaches only the holder's own records (SDD §11.4 step 3). */
    ownRecordsOnly: z.boolean().optional(),
};
const categoryMatchesAction = (value) => (value.action === 'view_unmasked') === (value.sensitivityCategory !== undefined);
const CATEGORY_RULE = 'a sensitivity category belongs to view_unmasked, and view_unmasked requires one';
export const GrantPermissionRequestSchema = z
    .strictObject(GrantPermissionFields)
    .refine(categoryMatchesAction, CATEGORY_RULE);
/** One permission granted to several roles in one change (SDD §11.4); each grant is audited. */
export const GrantPermissionToRolesRequestSchema = z
    .strictObject({ ...GrantPermissionFields, roleCodes: z.array(CodeSchema).min(1).max(200) })
    .refine(categoryMatchesAction, CATEGORY_RULE);
/** The roles that received the permission, and those that already held it (left unchanged). */
export const GrantPermissionToRolesResultSchema = z.object({
    granted: z.array(z.object({ roleCode: z.string(), id: IdSchema })),
    skipped: z.array(z.string()),
});
export const AssignRoleRequestSchema = z.strictObject({
    roleCode: CodeSchema,
    effectiveFrom: z.iso.date(),
    effectiveTo: z.iso.date().optional(),
    departmentId: IdSchema.optional(),
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    locationId: IdSchema.optional(),
    /** One client: the grant reaches that client and its records only (FRD-CRM-051). */
    clientId: IdSchema.optional(),
});
/**
 * A change to a user (SDD §11.5 administration; §7.3 requires If-Match): its status, its Employee
 * link (FRD-USR-002, FRD-HRP-011; `null` removes the link), or both.
 */
export const UpdateUserRequestSchema = z
    .strictObject({
    status: z.enum(['active', 'inactive']).optional(),
    employeeId: IdSchema.nullable().optional(),
    /** The name shown for the user. The email is the sign-in binding and is not changed here. */
    displayName: z.string().trim().min(1).max(200).optional(),
})
    .refine((value) => value.status !== undefined ||
    value.employeeId !== undefined ||
    value.displayName !== undefined, 'a change names the status, the employee link or the display name');
export const UserSummarySchema = z.object({
    id: IdSchema,
    email: z.email(),
    displayName: z.string(),
    status: z.enum(['active', 'inactive']),
    employeeId: IdSchema.nullable(),
    /** Sent back as `If-Match` on a status change (SDD §7.3); issued by the API, never invented. */
    version: z.number().int(),
});
/** `GET /api/v1/platform/roles` (SDD §11.5). */
export const RoleSummarySchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    description: z.string(),
    isPrivileged: z.boolean(),
    status: z.string(),
    grantCount: z.number().int(),
    /** Sent back as `If-Match` on a role change (SDD §7.3). */
    version: z.number().int(),
});
/** `GET /api/v1/platform/roles/{code}/permission-grants` (SDD §11.4). */
export const PermissionGrantSummarySchema = z.object({
    id: IdSchema,
    recordType: z.string(),
    action: z.string(),
    field: z.string().nullable(),
    sensitivityCategory: z.string().nullable(),
    ownRecordsOnly: z.boolean(),
});
/** `GET /api/v1/platform/users/{id}/role-assignments` (ERD §8.1): history included. */
export const RoleAssignmentSummarySchema = z.object({
    id: IdSchema,
    roleCode: z.string(),
    roleName: z.string(),
    isPrivileged: z.boolean(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
    departmentId: IdSchema.nullable(),
    costCenterId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    clientId: IdSchema.nullable(),
    current: z.boolean(),
    /** Sent back as `If-Match` when the assignment is ended (SDD §7.3). */
    version: z.number().int(),
});
/**
 * A change to a role (FRD-USR-005): its name, description or status. The code and whether it is
 * privileged are fixed at creation. Requires `If-Match`.
 */
export const UpdateRoleRequestSchema = z
    .strictObject({
    name: z.string().trim().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
    status: z.enum(['active', 'inactive']).optional(),
})
    .refine((value) => value.name !== undefined || value.description !== undefined || value.status !== undefined, 'a change names the name, the description or the status');
/**
 * Ends a role assignment (ERD §8.1: end-dated, never deleted). `effectiveTo` is the last day it is
 * effective; omitted, it ends at once. Requires `If-Match`.
 */
export const EndRoleAssignmentRequestSchema = z.strictObject({
    effectiveTo: z.iso.date().optional(),
});
/** `GET /api/v1/platform/roles/{code}/assignments`: who holds a role, history included. */
export const RoleHolderSummarySchema = z.object({
    id: IdSchema,
    userId: IdSchema,
    userEmail: z.string(),
    userDisplayName: z.string(),
    userStatus: z.string(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
    departmentId: IdSchema.nullable(),
    costCenterId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    clientId: IdSchema.nullable(),
    current: z.boolean(),
});
/** `GET /api/v1/platform/permission-catalogue`: the permissions declared in code (SDD §11.4). */
export const PermissionDeclarationSchema = z.object({
    recordType: z.string(),
    action: PermissionActionSchema,
    owningModule: z.string(),
    description: z.string(),
    privileged: z.boolean(),
});
//# sourceMappingURL=identity.js.map